import { useState, useEffect, useCallback } from 'react';
import {
  Rocket, Plus, ChevronDown, ChevronRight, Check, X, AlertCircle,
  DollarSign, Calendar, Pause, Play, XCircle, ArrowUpRight,
  Edit3, Save, RotateCcw, Zap, Snowflake, Clock, Target,
  ChevronLeft,
} from 'lucide-react';
import {
  fetchApi,
  getDeploymentPlans,
  getDeploymentPlan,
  getEligibleTranches,
  createDeploymentPlan,
  updateDeploymentPlan,
  pauseDeploymentPlan,
  resumeDeploymentPlan,
  cancelDeploymentPlan,
  completeDeploymentPlan,
  addDeploymentTranche,
  promoteTranche,
  skipTranche,
  unskipTranche,
  updateTranche,
} from '../api/client';
import Pipeline from './Pipeline';
import DeploymentOscillatorStrip from './DeploymentOscillatorStrip';
import useOscillatorSignal from '../hooks/useOscillatorSignal';
import { zoneTokens, trancheBadge } from '../lib/oscillatorZone';

function fmt$(v) {
  if (v == null) return 'N/A';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
}
function fmt$2(v) {
  if (v == null) return 'N/A';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
}
function fmtPct(v) {
  if (v == null) return '';
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
}
function fmtDate(d) {
  if (!d) return '';
  if (d.startsWith('2099')) return 'Not scheduled';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
}

const STATUS_COLORS = {
  draft: 'bg-slate-600/50 text-slate-400',
  active: 'bg-emerald-500/20 text-emerald-400',
  paused: 'bg-amber-500/20 text-amber-400',
  complete: 'bg-green-500/20 text-green-400',
  canceled: 'bg-red-500/20 text-red-400',
};

const TRANCHE_STATUS_COLORS = {
  pending: 'text-slate-400',
  eligible: 'text-emerald-400',
  frozen: 'text-amber-400',
  promoted: 'text-sky-400',
  executed: 'text-green-400',
  skipped: 'text-slate-500',
  canceled: 'text-red-400/60',
};

const TRANCHE_ICONS = {
  pending: Clock,
  eligible: Target,
  frozen: Snowflake,
  promoted: ArrowUpRight,
  executed: Check,
  skipped: RotateCcw,
  canceled: XCircle,
};

// ============================================================================
// Create Plan Form
// ============================================================================
function CreatePlanForm({ onCreated, onCancel }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({
    ticker: '', direction: 'entry', thesis: '', total_gap: '',
    account_id: '', price_ceiling: '', acceleration_trigger_price: '',
    deployment_pace: 'weekly', start_date: '', category_fit: '',
    target_weight: '', notes: '', status: 'active',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchApi('/accounts').then(d => setAccounts(d.filter(a => !a.is_closed))).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, total_gap: parseFloat(form.total_gap) || 0 };
      if (form.price_ceiling) payload.price_ceiling = parseFloat(form.price_ceiling);
      if (form.acceleration_trigger_price) payload.acceleration_trigger_price = parseFloat(form.acceleration_trigger_price);
      if (form.target_weight) payload.target_weight = parseFloat(form.target_weight);
      const plan = await createDeploymentPlan(payload);
      onCreated(plan);
    } catch (err) {
      alert(`Error creating plan: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const field = (label, name, type = 'text', opts = {}) => (
    <div className={opts.wide ? 'col-span-2' : ''}>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      {opts.textarea ? (
        <textarea
          className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-1.5 text-sm text-slate-200 focus:border-sky-500 focus:outline-none"
          rows={2}
          value={form[name]}
          onChange={e => setForm(f => ({ ...f, [name]: e.target.value }))}
          required={opts.required}
        />
      ) : opts.select ? (
        <select
          className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-1.5 text-sm text-slate-200 focus:border-sky-500 focus:outline-none"
          value={form[name]}
          onChange={e => setForm(f => ({ ...f, [name]: e.target.value }))}
          required={opts.required}
        >
          {opts.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input
          type={type}
          className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-1.5 text-sm text-slate-200 focus:border-sky-500 focus:outline-none"
          value={form[name]}
          onChange={e => setForm(f => ({ ...f, [name]: e.target.value }))}
          required={opts.required}
        />
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 mb-6">
      <h3 className="text-sm font-semibold text-slate-200 mb-3">New Deployment Plan</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {field('Ticker', 'ticker', 'text', { required: true })}
        {field('Direction', 'direction', 'text', { select: true, required: true, options: [{ value: 'entry', label: 'Entry (Buy)' }, { value: 'exit', label: 'Exit (Sell)' }] })}
        {field('Total Gap ($)', 'total_gap', 'number', { required: true })}
        {field('Account', 'account_id', 'text', { select: true, required: true, options: [{ value: '', label: 'Select...' }, ...accounts.map(a => ({ value: a.id, label: a.name || a.id }))] })}
        {field('Thesis', 'thesis', 'text', { wide: true, textarea: true, required: true })}
        {field('Price Ceiling', 'price_ceiling', 'number')}
        {field('Acceleration Trigger', 'acceleration_trigger_price', 'number')}
        {field('Pace', 'deployment_pace', 'text', { select: true, options: [{ value: 'weekly', label: 'Weekly (4 tranches)' }, { value: 'biweekly', label: 'Biweekly (3)' }, { value: 'monthly', label: 'Monthly (3)' }, { value: 'manual', label: 'Manual (1)' }, { value: 'opportunistic', label: 'Opportunistic (price-gated)' }] })}
        {field('Start Date', 'start_date', 'date')}
        {field('Target Weight %', 'target_weight', 'number')}
        {field('Category', 'category_fit')}
        {field('Status', 'status', 'text', { select: true, options: [{ value: 'draft', label: 'Draft' }, { value: 'active', label: 'Active' }] })}
        {field('Notes', 'notes', 'text', { wide: true, textarea: true })}
      </div>
      <div className="flex gap-2 mt-3">
        <button type="submit" disabled={saving} className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-500 disabled:opacity-50">
          {saving ? 'Creating...' : 'Create Plan'}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 bg-slate-700 text-slate-300 text-sm rounded hover:bg-slate-600">Cancel</button>
      </div>
    </form>
  );
}

// ============================================================================
// Editable Field
// ============================================================================
function EditableField({ label, value, onSave, type = 'text', textarea = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  const save = () => {
    onSave(type === 'number' ? (parseFloat(draft) || null) : draft);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="group flex items-start gap-1">
        <span className="text-xs text-slate-500">{label}:</span>
        <span className="text-xs text-slate-300 flex-1">{value ?? '—'}</span>
        <button onClick={() => { setDraft(value || ''); setEditing(true); }} className="opacity-0 group-hover:opacity-100 transition-opacity">
          <Edit3 className="w-3 h-3 text-slate-500 hover:text-sky-400" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-1">
      <span className="text-xs text-slate-500">{label}:</span>
      {textarea ? (
        <textarea className="flex-1 bg-slate-800 border border-sky-600 rounded px-1.5 py-0.5 text-xs text-slate-200" rows={2} value={draft} onChange={e => setDraft(e.target.value)} autoFocus />
      ) : (
        <input type={type} className="flex-1 bg-slate-800 border border-sky-600 rounded px-1.5 py-0.5 text-xs text-slate-200 w-20" value={draft} onChange={e => setDraft(e.target.value)} autoFocus />
      )}
      <button onClick={save} className="text-emerald-400 hover:text-emerald-300"><Save className="w-3 h-3" /></button>
      <button onClick={() => setEditing(false)} className="text-slate-500 hover:text-slate-400"><X className="w-3 h-3" /></button>
    </div>
  );
}

// ============================================================================
// Tranche Table (editable)
// ============================================================================
function TrancheRow({ t, onPromote, onSkip, onUnskip, onSave, pace }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const editable = ['pending', 'eligible', 'frozen'].includes(t.status);
  const isOpportunistic = pace === 'opportunistic';

  const startEdit = () => {
    setDraft({
      dollar_amount: t.dollar_amount || '',
      eligible_date: (t.eligible_date || '').startsWith('2099') ? '' : t.eligible_date || '',
      price_ceiling: t.price_ceiling || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    const updates = {};
    const amt = parseFloat(draft.dollar_amount);
    if (!isNaN(amt) && amt !== t.dollar_amount) updates.dollar_amount = amt;
    if (!isOpportunistic && draft.eligible_date && draft.eligible_date !== t.eligible_date) updates.eligible_date = draft.eligible_date;
    const ceil = draft.price_ceiling ? parseFloat(draft.price_ceiling) : null;
    if (ceil !== t.price_ceiling) updates.price_ceiling = ceil;
    if (Object.keys(updates).length > 0) {
      await onSave(t.id, updates);
    }
    setEditing(false);
  };

  const Icon = TRANCHE_ICONS[t.status] || Clock;

  if (editing) {
    return (
      <div className="flex items-center gap-2 py-2 px-3 rounded bg-slate-700/50 border border-sky-600/50">
        <div className="w-6 text-center text-xs text-slate-500">T{t.tranche_number}</div>
        <div className="flex items-center gap-2 flex-1">
          <div>
            <label className="block text-[10px] text-slate-500">Amount ($)</label>
            <input type="number" className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-200 w-20" value={draft.dollar_amount} onChange={e => setDraft(d => ({ ...d, dollar_amount: e.target.value }))} />
          </div>
          {!isOpportunistic && (
            <div>
              <label className="block text-[10px] text-slate-500">Eligible Date</label>
              <input type="date" className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-200" value={draft.eligible_date} onChange={e => setDraft(d => ({ ...d, eligible_date: e.target.value }))} />
            </div>
          )}
          <div>
            <label className="block text-[10px] text-slate-500">{isOpportunistic ? 'Target Price ($)' : 'Ceiling ($)'}</label>
            <input type="number" step="0.01" className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-200 w-20" value={draft.price_ceiling} onChange={e => setDraft(d => ({ ...d, price_ceiling: e.target.value }))} />
          </div>
        </div>
        <button onClick={saveEdit} className="px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-500 flex items-center gap-1">
          <Save className="w-3 h-3" /> Save
        </button>
        <button onClick={() => setEditing(false)} className="px-2 py-1 bg-slate-600 text-slate-300 text-xs rounded hover:bg-slate-500">
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 py-2 px-3 rounded ${
      t.status === 'eligible' ? 'bg-emerald-500/10 border-l-2 border-emerald-500' :
      t.status === 'frozen' ? 'bg-amber-500/10' :
      t.status === 'executed' ? 'bg-slate-700/30 opacity-70' :
      t.status === 'skipped' ? 'opacity-50' :
      t.status === 'canceled' ? 'opacity-30 line-through' :
      'bg-slate-800/50'
    }`}>
      <div className="w-6 text-center text-xs text-slate-500">T{t.tranche_number}</div>
      <Icon className={`w-3.5 h-3.5 ${TRANCHE_STATUS_COLORS[t.status]}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-mono text-slate-200">{fmt$(t.dollar_amount)}</span>
          {t.price_ceiling && <span className="text-xs text-slate-500">{isOpportunistic ? '@' : '≤'}{fmt$2(t.price_ceiling)}</span>}
          {isOpportunistic ? (
            <span className="text-xs text-purple-400 italic">Price-gated</span>
          ) : (
            <span className={`text-xs ${(t.eligible_date || '').startsWith('2099') ? 'text-amber-400 italic' : 'text-slate-500'}`}>
              {fmtDate(t.eligible_date)}
            </span>
          )}
          <span className={`text-xs capitalize ${TRANCHE_STATUS_COLORS[t.status]}`}>{t.status}</span>
          {t.executed_price != null && <span className="text-xs text-green-400">{fmt$2(t.executed_price)} on {fmtDate(t.executed_at?.split('T')[0])}</span>}
          {t.trade_plan_id && <span className="text-xs text-sky-400">Plan #{t.trade_plan_id}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {t.status === 'eligible' && (
          <>
            <button onClick={() => onPromote(t.id)} className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30 flex items-center gap-1">
              <ArrowUpRight className="w-3 h-3" /> Promote
            </button>
            <button onClick={() => onSkip(t.id)} className="text-xs px-2 py-1 bg-slate-700 text-slate-400 rounded hover:bg-slate-600">Skip</button>
          </>
        )}
        {t.status === 'skipped' && (
          <button onClick={() => onUnskip(t.id)} className="text-xs px-2 py-1 bg-slate-700 text-slate-400 rounded hover:bg-slate-600 flex items-center gap-1">
            <RotateCcw className="w-3 h-3" /> Unskip
          </button>
        )}
        {editable && (
          <button onClick={startEdit} className="text-xs px-2 py-1 bg-slate-700/80 text-slate-400 rounded hover:bg-slate-600 hover:text-sky-400 flex items-center gap-1">
            <Edit3 className="w-3 h-3" /> Edit
          </button>
        )}
      </div>
    </div>
  );
}

function TrancheTable({ tranches, onPromote, onSkip, onUnskip, onSave, pace }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 mb-4">
      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
        Tranches
        {pace === 'opportunistic' && <span className="ml-2 text-purple-400 font-normal normal-case">(price-gated)</span>}
      </h3>
      {tranches.length === 0 ? (
        <p className="text-xs text-slate-500 italic">{pace === 'opportunistic' ? 'No tranches yet — add price-gated tranches below' : 'No tranches yet'}</p>
      ) : (
        <div className="space-y-1">
          {tranches.map(t => (
            <TrancheRow key={t.id} t={t} onPromote={onPromote} onSkip={onSkip} onUnskip={onUnskip} onSave={onSave} pace={pace} />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Plan Detail View
// ============================================================================
function PlanDetail({ planId, onBack, onRefresh }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingTranche, setAddingTranche] = useState(false);
  const [newTranche, setNewTranche] = useState({ dollar_amount: '', eligible_date: '', price_ceiling: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDeploymentPlan(planId);
      setPlan(data);
    } catch (err) {
      console.error('Error loading plan:', err);
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-slate-400 text-sm py-8 text-center">Loading plan...</div>;
  if (!plan) return <div className="text-red-400 text-sm py-8 text-center">Plan not found</div>;

  const handleFieldSave = async (field, value) => {
    await updateDeploymentPlan(plan.id, { [field]: value });
    load();
  };

  const handleAction = async (action) => {
    try {
      if (action === 'pause') await pauseDeploymentPlan(plan.id);
      else if (action === 'resume') await resumeDeploymentPlan(plan.id);
      else if (action === 'cancel') {
        const reason = prompt('Cancel reason:');
        if (!reason) return;
        await cancelDeploymentPlan(plan.id, reason);
      }
      else if (action === 'complete') await completeDeploymentPlan(plan.id);
      load();
      onRefresh();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handlePromote = async (trancheId) => {
    if (!confirm('Promote this tranche to a trade plan?')) return;
    try {
      await promoteTranche(trancheId);
      load();
      onRefresh();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleSkip = async (trancheId) => {
    const notes = prompt('Skip reason (optional):');
    try {
      await skipTranche(trancheId, notes);
      load();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleUnskip = async (trancheId) => {
    try {
      await unskipTranche(trancheId);
      load();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleAddTranche = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        dollar_amount: parseFloat(newTranche.dollar_amount),
        price_ceiling: newTranche.price_ceiling ? parseFloat(newTranche.price_ceiling) : undefined,
      };
      if (plan.deployment_pace !== 'opportunistic' && newTranche.eligible_date) {
        payload.eligible_date = newTranche.eligible_date;
      }
      await addDeploymentTranche(plan.id, payload);
      setAddingTranche(false);
      setNewTranche({ dollar_amount: '', eligible_date: '', price_ceiling: '' });
      load();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleTrancheFieldSave = async (trancheId, updates) => {
    await updateTranche(trancheId, typeof updates === 'object' ? updates : {});
    load();
  };

  const progressPct = plan.total_gap > 0 ? Math.min(100, (plan.deployed_amount / plan.total_gap) * 100) : 0;

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-400 hover:text-sky-400 mb-4">
        <ChevronLeft className="w-4 h-4" /> Back to Deploy
      </button>

      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold text-slate-200">
          {plan.ticker} Round {plan.round_number} — <span className="capitalize">{plan.direction}</span>
        </h2>
        <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[plan.status] || 'bg-slate-700 text-slate-400'}`}>
          {plan.status}
        </span>
      </div>

      {/* Plan Summary */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 mb-4">
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Plan Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-4">
          <EditableField label="Target weight" value={plan.target_weight != null ? `${plan.target_weight}%` : null} onSave={v => handleFieldSave('target_weight', parseFloat(v))} />
          <div className="text-xs"><span className="text-slate-500">Current weight:</span> <span className="text-slate-300">{plan.current_weight != null ? `${plan.current_weight}%` : '—'}</span></div>
          <div className="text-xs"><span className="text-slate-500">Gap:</span> <span className="text-slate-300">{fmt$(plan.total_gap)}</span></div>
          <EditableField label="Price ceiling" value={plan.price_ceiling != null ? `$${plan.price_ceiling}` : null} onSave={v => handleFieldSave('price_ceiling', parseFloat(v?.replace('$', '')))} />
          <EditableField label="Acceleration" value={plan.acceleration_trigger_price != null ? `$${plan.acceleration_trigger_price}` : null} onSave={v => handleFieldSave('acceleration_trigger_price', parseFloat(v?.replace('$', '')))} />
          <div className="text-xs"><span className="text-slate-500">Pace:</span> <span className="text-slate-300 capitalize">{plan.deployment_pace}</span></div>
          <div className="text-xs"><span className="text-slate-500">Account:</span> <span className="text-slate-300">{plan.account_id}</span></div>
          <EditableField label="Category" value={plan.category_fit} onSave={v => handleFieldSave('category_fit', v)} />
          <div className="text-xs"><span className="text-slate-500">Current price:</span> <span className="text-slate-300">{plan.current_price ? fmt$2(plan.current_price) : '—'}</span></div>
        </div>
      </div>

      {/* Thesis */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 mb-4">
        <EditableField label="Thesis" value={plan.thesis} onSave={v => handleFieldSave('thesis', v)} textarea />
      </div>

      {/* Tranches */}
      <TrancheTable
        tranches={plan.tranches || []}
        onPromote={handlePromote}
        onSkip={handleSkip}
        onUnskip={handleUnskip}
        onSave={handleTrancheFieldSave}
        pace={plan.deployment_pace}
      />

      {/* Progress bar */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
          <span>Deployed: {fmt$(plan.deployed_amount)} / {fmt$(plan.total_gap)} ({progressPct.toFixed(0)}%)</span>
        </div>
        <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
        </div>

        {/* Add tranche */}
        {addingTranche ? (
          <form onSubmit={handleAddTranche} className="mt-3 flex items-end gap-2">
            <div>
              <label className="block text-xs text-slate-500">Amount</label>
              <input type="number" className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-slate-200 w-24" value={newTranche.dollar_amount} onChange={e => setNewTranche(t => ({ ...t, dollar_amount: e.target.value }))} required />
            </div>
            {plan.deployment_pace !== 'opportunistic' && (
              <div>
                <label className="block text-xs text-slate-500">Date</label>
                <input type="date" className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-slate-200" value={newTranche.eligible_date} onChange={e => setNewTranche(t => ({ ...t, eligible_date: e.target.value }))} required />
              </div>
            )}
            <div>
              <label className="block text-xs text-slate-500">{plan.deployment_pace === 'opportunistic' ? 'Target Price' : 'Ceiling'}</label>
              <input type="number" step="0.01" className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm text-slate-200 w-24" value={newTranche.price_ceiling} onChange={e => setNewTranche(t => ({ ...t, price_ceiling: e.target.value }))} required={plan.deployment_pace === 'opportunistic'} />
            </div>
            <button type="submit" className="px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-500">Add</button>
            <button type="button" onClick={() => setAddingTranche(false)} className="px-2 py-1 bg-slate-700 text-slate-400 text-xs rounded hover:bg-slate-600">Cancel</button>
          </form>
        ) : (
          <button onClick={() => setAddingTranche(true)} className="mt-3 flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300">
            <Plus className="w-3 h-3" /> Add Tranche
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        {plan.status === 'active' && (
          <button onClick={() => handleAction('pause')} className="flex items-center gap-1 px-3 py-1.5 bg-amber-500/20 text-amber-400 text-sm rounded hover:bg-amber-500/30">
            <Pause className="w-3.5 h-3.5" /> Pause Plan
          </button>
        )}
        {plan.status === 'paused' && (
          <button onClick={() => handleAction('resume')} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 text-emerald-400 text-sm rounded hover:bg-emerald-500/30">
            <Play className="w-3.5 h-3.5" /> Resume Plan
          </button>
        )}
        {!['complete', 'canceled'].includes(plan.status) && (
          <>
            <button onClick={() => handleAction('cancel')} className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 text-sm rounded hover:bg-red-500/30">
              <XCircle className="w-3.5 h-3.5" /> Cancel Plan
            </button>
            <button onClick={() => handleAction('complete')} className="flex items-center gap-1 px-3 py-1.5 bg-green-500/20 text-green-400 text-sm rounded hover:bg-green-500/30">
              <Check className="w-3.5 h-3.5" /> Complete Plan
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================
export default function DeploymentPlans() {
  const [plans, setPlans] = useState([]);
  const [eligible, setEligible] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState('active');
  const [showCreate, setShowCreate] = useState(false);
  const [detailPlanId, setDetailPlanId] = useState(null);
  const [expandedPlans, setExpandedPlans] = useState(new Set());
  const [showLegacy, setShowLegacy] = useState(false);
  const { signal: oscSignal } = useOscillatorSignal();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [planData, eligData] = await Promise.all([
        getDeploymentPlans({ status: 'all' }),
        getEligibleTranches(),
      ]);
      setPlans(planData);
      setEligible(eligData);
    } catch (err) {
      console.error('Error fetching deployment plans:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (detailPlanId) {
    return <PlanDetail planId={detailPlanId} onBack={() => { setDetailPlanId(null); fetchAll(); }} onRefresh={fetchAll} />;
  }

  if (showLegacy) {
    return (
      <div>
        <button onClick={() => setShowLegacy(false)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-sky-400 mb-4">
          <ChevronLeft className="w-4 h-4" /> Back to Deploy
        </button>
        <Pipeline />
      </div>
    );
  }

  const statusCounts = {
    active: plans.filter(p => p.status === 'active').length,
    paused: plans.filter(p => p.status === 'paused').length,
    draft: plans.filter(p => p.status === 'draft').length,
    complete: plans.filter(p => p.status === 'complete').length,
    all: plans.length,
  };

  const filteredPlans = statusTab === 'all' ? plans : plans.filter(p => p.status === statusTab);

  const eligibleTranches = eligible?.eligible_tranches || [];
  const frozenTranches = eligible?.frozen_tranches || [];
  const accelerationAlerts = eligible?.acceleration_alerts || [];

  const upcomingTranches = [];
  const activePlans = plans.filter(p => p.status === 'active');
  for (const plan of activePlans) {
    if (plan.deployment_pace === 'opportunistic') continue;
    for (const t of (plan.tranches || [])) {
      if (t.status === 'pending' && t.eligible_date && !t.eligible_date.startsWith('2099')) {
        upcomingTranches.push({ ...t, ticker: plan.ticker, direction: plan.direction, round_number: plan.round_number, plan_id: plan.id });
      }
    }
  }
  upcomingTranches.sort((a, b) => (a.eligible_date || '').localeCompare(b.eligible_date || ''));

  const handlePromote = async (trancheId) => {
    if (!confirm('Promote this tranche to a trade plan?')) return;
    try {
      await promoteTranche(trancheId);
      fetchAll();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleSkip = async (trancheId) => {
    try {
      await skipTranche(trancheId);
      fetchAll();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const toggleExpand = (planId) => {
    const next = new Set(expandedPlans);
    next.has(planId) ? next.delete(planId) : next.add(planId);
    setExpandedPlans(next);
  };

  if (loading) {
    return <div className="text-slate-400 text-sm py-8 text-center">Loading deployment plans...</div>;
  }

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-200 flex items-center gap-2">
          <Rocket className="w-5 h-5 text-sky-400" /> Deploy
        </h2>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-500">
          <Plus className="w-4 h-4" /> New Plan
        </button>
      </div>

      {showCreate && (
        <CreatePlanForm
          onCreated={() => { setShowCreate(false); fetchAll(); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Status Tabs */}
      <div className="flex gap-1 mb-4 border-b border-slate-700 pb-2">
        {['active', 'paused', 'draft', 'complete', 'all'].map(tab => (
          <button
            key={tab}
            onClick={() => setStatusTab(tab)}
            className={`px-3 py-1 text-sm rounded-t capitalize ${
              statusTab === tab ? 'bg-slate-700 text-slate-200 font-medium' : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            {tab} {statusCounts[tab] > 0 && <span className="ml-1 text-xs opacity-60">({statusCounts[tab]})</span>}
          </button>
        ))}
      </div>

      {/* Market oscillator context (§11.3) */}
      {statusTab === 'active' && (
        <DeploymentOscillatorStrip eligibleCount={eligibleTranches.length} />
      )}

      {/* Eligible Today */}
      {statusTab === 'active' && eligibleTranches.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Eligible Today
          </h3>
          <div className="space-y-2">
            {eligibleTranches.map(t => {
              const withinCeilingPct =
                t.current_price && t.price_ceiling
                  ? ((t.price_ceiling - t.current_price) / t.current_price) * 100
                  : null;
              const badge = oscSignal
                ? trancheBadge(oscSignal.value, withinCeilingPct)
                : null;
              const badgeTokens = badge ? zoneTokens(badge.tone) : null;
              return (
                <div key={t.tranche_id} className="bg-slate-800/60 border-l-2 border-emerald-500 rounded-r-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => setDetailPlanId(t.plan_id)} className="text-sm font-medium text-slate-200 hover:text-sky-400">
                          {t.ticker} Round {t.round_number} — <span className="capitalize">{t.direction}</span>
                        </button>
                        {badge && badgeTokens && (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${badgeTokens.bgSoft} ${badgeTokens.borderClass} ${badgeTokens.textClass}`}
                            title={`Oscillator ${Number(oscSignal.value).toFixed(1)} · ${badge.label}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${badgeTokens.dotClass}`} />
                            {badge.label}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        Tranche {t.tranche_number} · {fmt$(t.dollar_amount)} · {t.pace === 'opportunistic' ? 'price-gated' : `eligible ${fmtDate(t.eligible_date)}`} · {t.account_id}
                      </div>
                      {t.current_price && t.price_ceiling && (
                        <div className="text-xs mt-0.5">
                          <span className="text-slate-500">Current:</span>{' '}
                          <span className={t.price_vs_ceiling === 'below' ? 'text-emerald-400' : 'text-red-400'}>
                            {fmt$2(t.current_price)}
                          </span>
                          <span className="text-slate-500"> ({((t.price_ceiling - t.current_price) / t.current_price * 100).toFixed(1)}% {t.price_vs_ceiling} ≤{fmt$2(t.price_ceiling)} ceiling)</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handlePromote(t.tranche_id)} className="px-2.5 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded hover:bg-emerald-500/30 flex items-center gap-1">
                        <ArrowUpRight className="w-3 h-3" /> Promote to Trade Plan
                      </button>
                      <button onClick={() => handleSkip(t.tranche_id)} className="px-2 py-1 bg-slate-700 text-slate-400 text-xs rounded hover:bg-slate-600">
                        Skip
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upcoming */}
      {statusTab === 'active' && upcomingTranches.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Upcoming</h3>
          <div className="space-y-1">
            {upcomingTranches.slice(0, 10).map(t => (
              <div key={t.id} className="flex items-center gap-3 px-3 py-1.5 bg-slate-800/40 rounded text-sm">
                <button onClick={() => setDetailPlanId(t.plan_id)} className="text-slate-300 hover:text-sky-400 font-medium min-w-[100px]">
                  {t.ticker} R{t.round_number}
                </button>
                <span className="text-slate-500 text-xs">T{t.tranche_number}:</span>
                <span className="text-slate-300 font-mono text-xs">{fmt$(t.dollar_amount)}</span>
                <span className="text-slate-500 text-xs flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {fmtDate(t.eligible_date)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Frozen */}
      {statusTab === 'active' && frozenTranches.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Snowflake className="w-3 h-3" /> Frozen (Earnings)
          </h3>
          <div className="space-y-1">
            {frozenTranches.map(t => (
              <div key={t.tranche_id} className="flex items-center gap-3 px-3 py-1.5 bg-amber-500/5 border-l-2 border-amber-500/50 rounded-r text-sm">
                <span className="text-slate-300 font-medium">{t.ticker}</span>
                <span className="text-slate-500 text-xs">T{t.tranche_number}: {fmt$(t.dollar_amount)}</span>
                <span className="text-amber-400 text-xs">frozen until {fmtDate(t.thaw_date)}</span>
                {t.earnings_date && <span className="text-slate-500 text-xs">(earnings {fmtDate(t.earnings_date)})</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Acceleration Alerts */}
      {statusTab === 'active' && accelerationAlerts.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Zap className="w-3 h-3" /> Acceleration Alerts
          </h3>
          <div className="space-y-1">
            {accelerationAlerts.map(a => (
              <div key={a.plan_id} className="px-3 py-2 bg-red-500/10 border-l-2 border-red-500/50 rounded-r text-sm">
                <span className="text-red-400 font-medium">{a.ticker}</span>
                <span className="text-slate-400 text-xs ml-2">
                  dropped below {fmt$2(a.trigger_price)} trigger (current: {fmt$2(a.current_price)})
                </span>
                {a.accelerated_tranche_id && (
                  <span className="text-red-300 text-xs ml-2">— tranche accelerated to today</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Plans */}
      {filteredPlans.length === 0 ? (
        <div className="text-slate-500 text-sm py-4 text-center">No {statusTab === 'all' ? '' : statusTab} plans</div>
      ) : (
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            {statusTab === 'all' ? 'All' : statusTab.charAt(0).toUpperCase() + statusTab.slice(1)} Plans ({filteredPlans.length})
          </h3>
          <div className="space-y-1">
            {filteredPlans.map(plan => (
              <div key={plan.id} className={`bg-slate-800/40 rounded border border-slate-700/50 ${plan.status === 'canceled' ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-800/60" onClick={() => toggleExpand(plan.id)}>
                  {expandedPlans.has(plan.id) ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <button onClick={e => { e.stopPropagation(); setDetailPlanId(plan.id); }} className="text-sm font-medium text-slate-200 hover:text-sky-400">
                    {plan.ticker} Round {plan.round_number}
                  </button>
                  <span className="text-xs text-slate-500 capitalize">{plan.direction}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_COLORS[plan.status]}`}>{plan.status}</span>
                  {plan.deployment_pace === 'opportunistic' && <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">Price-gated</span>}
                  <span className="text-xs text-slate-500 ml-auto">{fmt$(plan.total_gap)}</span>
                  {plan.price_ceiling && <span className="text-xs text-slate-500">≤{fmt$2(plan.price_ceiling)}</span>}
                  <span className="text-xs text-slate-500">{plan.deploy_pct}% deployed</span>
                </div>
                {expandedPlans.has(plan.id) && (
                  <div className="px-3 pb-2 border-t border-slate-700/50 pt-2">
                    <div className="text-xs text-slate-400 mb-2 line-clamp-2">{plan.thesis}</div>
                    <div className="space-y-0.5">
                      {(plan.tranches || []).map(t => {
                        const Icon = TRANCHE_ICONS[t.status] || Clock;
                        return (
                          <div key={t.id} className="flex items-center gap-2 text-xs">
                            <Icon className={`w-3 h-3 ${TRANCHE_STATUS_COLORS[t.status]}`} />
                            <span className="w-6 text-slate-500">T{t.tranche_number}</span>
                            <span className="font-mono text-slate-300">{fmt$(t.dollar_amount)}</span>
                            {plan.deployment_pace === 'opportunistic' ? (
                              <span className="text-purple-400">{t.price_ceiling ? `@${fmt$2(t.price_ceiling)}` : 'Price-gated'}</span>
                            ) : (
                              <span className="text-slate-500">{fmtDate(t.eligible_date)}</span>
                            )}
                            <span className={`capitalize ${TRANCHE_STATUS_COLORS[t.status]}`}>{t.status}</span>
                            {t.status === 'eligible' && (
                              <button onClick={e => { e.stopPropagation(); handlePromote(t.id); }} className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30 ml-auto">Promote</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legacy link */}
      <div className="mt-8 pt-4 border-t border-slate-700/50">
        <button onClick={() => setShowLegacy(true)} className="text-xs text-slate-500 hover:text-slate-400">
          Legacy Pipelines →
        </button>
      </div>
    </div>
  );
}
